import React, { useEffect, useRef } from 'react'
import Swiper from 'swiper'
import { Navigation, Pagination } from 'swiper/modules'

import Card from '@f/components/Card'
import CTA from '@f/elements/CTA'
import Icon from '@f/elements/Icon'

const CardCarousel = ({
  card,
  main_cta_link = null,
  main_cta_text = 'Alle anzeigen',
  items,
  item_nid_type = '',
  display_bullets = 'below',
  display_arrows = true,
  wtData = {},
  location = null,
  wtDisable = false,
  dataSel = '',
  ...props
}) => {
  let SwiperObject = null
  const CarouselRef = useRef(null)

  let swiperSettings = {
    centerInsufficientSlides: !!props.centerInsufficientSlides,
    containerModifierClass: 'CardCarousel-wrapper-',
    wrapperClass: 'CardCarousel-items-wrapper',
    slideClass: 'js-card',
    spaceBetween: 16,
    slidesPerView: 'auto',
    simulateTouch: false,
    watchOverflow: true,
    preventInteractionOnTransition: true,
    watchSlidesProgress: true,
    watchSlidesVisibility: true,
    slideVisibleClass: 'is-visible'
  }

  if (display_bullets) {
    swiperSettings = {
      ...swiperSettings,
      pagination: {
        el: '.CardCarousel-bullets',
        bulletClass: 'CardCarousel-bullet',
        bulletActiveClass: 'is-active',
        type: 'bullets',
        dynamicBullets: true,
        renderBullet: (index, className) =>
          `<div class='[ f-bo-round ] CardCarousel-bullet ${className}'></div>`
      }
    }
  }

  if (display_arrows) {
    swiperSettings = {
      ...swiperSettings,
      navigation: {
        nextEl: '.CardCarousel-arrow-next',
        prevEl: '.CardCarousel-arrow-prev',
        disabledClass: 'is-disabled'
      }
    }
  }

  useEffect(() => {
    const attachGalleries = async () => {
      Swiper.use([Navigation, Pagination])

      SwiperObject = new Swiper(CarouselRef.current, swiperSettings)

      if (!wtDisable) {
        const trackSlides = () => {
          let visibleIndices = []

          SwiperObject.slides.forEach((element, index) => {
            if (/is-visible/.test(element.className)) {
              visibleIndices.push(index)
            }
          })

          window._wt({
            category: 'item-collection',
            action: 'display',
            values: items
              .filter((element, index) => visibleIndices.indexOf(index) > -1)
              .map(element => ({ type: item_nid_type, value: element.nid })),
            ...(wtData['item-collection'] ? wtData['item-collection'] : {})
          })
        }

        /* Initial slides load */
        if (window.IntersectionObserver) {
          /* Modern browsers only track when carousel is in the viewport */
          const observer = new IntersectionObserver((entries, observer) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                trackSlides()
                observer.unobserve(entry.target)
              }
            }
          })

          observer.observe(CarouselRef.current)
        } else {
          /* IE */
          trackSlides()
        }

        SwiperObject.on('slideChangeTransitionEnd', function () {
          trackSlides()
        })
      }
    }
    attachGalleries()

    return SwiperObject
      ? () => {
          SwiperObject.destroy(true, true)
        }
      : undefined
  }, [])

  return (
    <>
      <div
        ref={CarouselRef}
        className={`[ f-pos-rel f-overflow ${
          display_bullets === 'below' ? 'f-p-b-040' : 'f-p-b-010'
        } ] CardCarousel-wrapper`}
        data-sel={dataSel}
        data-wtlab={
          wtData['item-collection'] ? wtData['item-collection'].label : ''
        }>
        <div className={`[ f-d-flr ] CardCarousel-items-wrapper`}>
          {items.slice(0, 8).map((item, index) => (
            <Card
              key={`card_carousel_${item.nid ? item.nid : item.id}_${index}`}
              tag='a'
              template={card}
              item={item}
              href={item.url}
              main_cta_link={main_cta_link}
              location={location}
              wtData={wtData}
            />
          ))}
        </div>

        {display_bullets ? <div className='CardCarousel-bullets' /> : null}

        {display_arrows ? (
          <>
            <CTA
              type='icon'
              tag='button'
              className='[ f-p-a-005 f-shadow f-bo-round ] CardCarousel-arrow CardCarousel-arrow-prev'
              wtDisable={true}>
              <Icon
                icon='left-link-16px'
                className='[ f-icon-030 f-c-st-bla ]'
              />
              <span className='[ f-visually-hidden ]'>Vorheriges Haus</span>
            </CTA>
            <CTA
              type='icon'
              tag='button'
              className='[ f-p-a-005 f-shadow f-bo-round ] CardCarousel-arrow CardCarousel-arrow-next'
              wtDisable={true}>
              <Icon
                icon='right-link-16px'
                className='[ f-icon-030 f-c-st-bla ]'
              />
              <span className='[ f-visually-hidden ]'>Nächstes Haus</span>
            </CTA>
          </>
        ) : null}
      </div>

      {main_cta_link ? (
        <div className='[ f-p-v-030 f-t-centre ]'>
          <CTA type='block-secondary' tag='a' href={main_cta_link}>
            {main_cta_text}
          </CTA>
        </div>
      ) : null}
    </>
  )
}

export default CardCarousel
